import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  GridLegacy as Grid
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  CameraAlt as CameraIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { MpesaPaymentDialog } from '../components/MpesaPaymentDialog';
import { mpesaService } from '../services/mpesaService';
import {
  SchoolSubscriptionStatus,
  SchoolSubscription as SchoolSubscriptionType,
  SubscriptionPlan,
  activateToken,
  getSchoolSubscriptionStatus,
  getSchoolSubscriptions
} from '../services/subscriptionService';

const SchoolSubscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<SchoolSubscriptionStatus | null>(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState<SchoolSubscriptionType[]>([]);
  const [tokenDialog, setTokenDialog] = useState(false);
  const [token, setToken] = useState('');
  const [activating, setActivating] = useState(false);
  
  // M-Pesa payment states
  const [mpesaStatus, setMpesaStatus] = useState<any>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SchoolSubscriptionType | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user?.school_id) {
      fetchSubscriptionData();
      fetchMpesaStatus();
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [statusData, historyData] = await Promise.all([
        getSchoolSubscriptionStatus(user!.school_id!),
        getSchoolSubscriptions(user!.school_id!)
      ]);



      setSubscriptionStatus(statusData as SchoolSubscriptionStatus | null);
      setSubscriptionHistory(historyData as any[]);
    } catch (err: any) {
      console.error('Failed to fetch subscription data:', err);
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMpesaStatus = async () => {
    try {
      const status = await mpesaService.getStatus();

      setMpesaStatus(status);
    } catch (err) {
      console.error('Failed to fetch M-Pesa status:', err);
    }
  };

  const handlePaymentInitiation = (subscription: SchoolSubscriptionType) => {
    setSelectedSubscription(subscription);
    setPaymentDialogOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentDialogOpen(false);
    setSuccess('Payment initiated successfully! Check your phone for M-Pesa prompt.');
    fetchSubscriptionData(); // Refresh data
  };

  const handleTokenActivation = async () => {
    if (!token.trim()) {
      setError('Please enter a valid token');
      return;
    }

    setActivating(true);
    setError('');
    
    try {
      await activateToken({
        token: token.trim(),
        school_id: user!.school_id!
      });
      
      setTokenDialog(false);
      setToken('');
      fetchSubscriptionData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to activate token');
    } finally {
      setActivating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'expired':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="School Subscription"
        subtitle="Manage your school's subscription and access"
        action={
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => setTokenDialog(true)}
            disabled={subscriptionStatus?.subscription_status === 'active'}
          >
            Activate Token
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* M-Pesa Status */}
      {mpesaStatus && (
        <Alert 
          severity={mpesaStatus.ready ? "success" : "warning"} 
          sx={{ mb: 2 }}
          icon={mpesaStatus.ready ? <CheckCircleIcon /> : <ErrorIcon />}
        >
          <Typography variant="body2">
            <strong>M-Pesa Status:</strong> {mpesaStatus.ready ? 'Ready for payments' : 'Not configured'}
          </Typography>
          {mpesaStatus.ready && (
            <Typography variant="body2">
              Environment: {mpesaStatus.environment} | Shortcode: {mpesaStatus.business_shortcode}
            </Typography>
          )}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}



      {/* Current Subscription Status */}
      {subscriptionStatus && (
        <>
          {subscriptionStatus.subscription_status === 'no_subscription' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your school does not have an active subscription. Please activate a subscription token to access all features.
            </Alert>
          )}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getStatusIcon(subscriptionStatus.subscription_status)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Current Subscription Status
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={subscriptionStatus.subscription_status}
                      color={getStatusColor(subscriptionStatus.subscription_status) as any}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  {subscriptionStatus.subscription_status !== 'no_subscription' && subscriptionStatus.active_subscription && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Plan
                        </Typography>
                        <Typography variant="body1">
                          {subscriptionStatus.active_subscription.plan?.name}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Subscription Type
                        </Typography>
                        <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                          {subscriptionStatus.active_subscription.subscription_type}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Valid Until
                        </Typography>
                        <Typography variant="body1">
                          {new Date(subscriptionStatus.active_subscription.end_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {subscriptionStatus.subscription_status === 'no_subscription' && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                      No active subscription. Please activate a subscription token to access features.
                    </Typography>
                  )}
                  {/* Payment Status for Active Subscription */}
                  {subscriptionStatus.subscription_status !== 'no_subscription' && 
                   subscriptionStatus.active_subscription?.payment_status === 'pending' && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Payment Status
                      </Typography>
                      <Chip
                        label="Payment Pending"
                        color="warning"
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {mpesaStatus?.ready && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PaymentIcon />}
                          onClick={() => handlePaymentInitiation(subscriptionStatus.active_subscription!)}
                          sx={{ mt: 1 }}
                        >
                          Pay Now
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Current Usage
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <PeopleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {subscriptionStatus.subscription_status === 'no_subscription' ? '0 / 0' : `${subscriptionStatus.current_student_count} / ${subscriptionStatus.max_students || '∞'}`} students
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CameraIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {subscriptionStatus.subscription_status === 'no_subscription' ? '0 / 0' : `${subscriptionStatus.current_camera_count} / ${subscriptionStatus.max_cameras || '∞'}`} cameras
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Access Status
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Students"
                        color={subscriptionStatus.subscription_status === 'no_subscription' ? 'error' : (subscriptionStatus.can_use_students ? 'success' : 'error')}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Cameras"
                        color={subscriptionStatus.subscription_status === 'no_subscription' ? 'error' : (subscriptionStatus.can_use_cameras ? 'success' : 'error')}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Subscription History */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Subscription History
          </Typography>
          
          {subscriptionHistory.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
              No subscription history found
            </Typography>
          ) : (
            <List>
              {subscriptionHistory.map((subscription, index) => (
                <React.Fragment key={subscription.id}>
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(subscription.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="subtitle1">
                            {subscription.plan?.name}
                          </Typography>
                          <Chip
                            label={subscription.status}
                            color={getStatusColor(subscription.status) as any}
                            size="small"
                          />
                          <Chip
                            label={subscription.subscription_type}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {new Date(subscription.start_date).toLocaleDateString()} - {new Date(subscription.end_date).toLocaleDateString()}
                          </Typography>
                          {subscription.activated_at && (
                            <Typography variant="body2" color="textSecondary">
                              Activated: {new Date(subscription.activated_at).toLocaleDateString()}
                            </Typography>
                          )}
                          {subscription.payment_status && (
                            <Typography variant="body2" color="textSecondary">
                              Payment: {subscription.payment_status}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {/* Payment Button for Pending Subscriptions */}
                    {subscription.payment_status === 'pending' && mpesaStatus?.ready && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PaymentIcon />}
                        onClick={() => handlePaymentInitiation(subscription)}
                        sx={{ ml: 2 }}
                      >
                        Pay Now
                      </Button>
                    )}
                  </ListItem>
                  {index < subscriptionHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Token Activation Dialog */}
      <Dialog open={tokenDialog} onClose={() => setTokenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Activate Subscription Token</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter the activation token provided by the system administrator to activate your school's subscription.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Activation Token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Enter your activation token"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleTokenActivation} 
            variant="contained"
            disabled={activating || !token.trim()}
          >
            {activating ? 'Activating...' : 'Activate Token'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* M-Pesa Payment Dialog */}
      {paymentDialogOpen && selectedSubscription && (
        <MpesaPaymentDialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
          onSuccess={handlePaymentSuccess}
          subscriptionId={selectedSubscription.id}
          amount={selectedSubscription.plan?.price_per_term || 0}
          title={`Pay for ${selectedSubscription.plan?.name} Subscription`}
          description={`Payment for ${selectedSubscription.subscription_type} subscription`}
        />
      )}
    </Box>
  );
};

export default SchoolSubscriptionPage; 